import React, { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import type { PaginationState, RowSelectionState, SortingState } from '@tanstack/react-table';
import { Button } from '@dashboard/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@dashboard/components/ui/select';
import { toast } from '@dashboard/context/ToastContext';
import { DataGrid } from '@dashboard/components/DataGrid';
import usePage from '@dashboard/hooks/usePage';
import {
  useStaffInvites,
  type StaffInvite,
  type StaffSortField,
  type StaffSortDir,
  type StaffStatusFilter,
} from '../hooks/useStaffInvites';
import { getStaffColumns, STATUS_LABEL, type RowPending } from './staffColumns';
import { deriveTicketTypeOptions } from './TicketTypeRestriction';

const URL_STATUS_TO_API: Record<string, StaffStatusFilter> = {
  invited: 'pending',
  accepted: 'active',
  removed: 'revoked',
};
const API_STATUS_TO_URL = {
  pending: 'invited',
  active: 'accepted',
  revoked: 'removed',
} as const;

interface Props {
  pageId: string;
  sellerId: string;
  canManage: boolean;
  title?: React.ReactNode;
  actions?: React.ReactNode;
}

const PAGE_SIZE = 20;

const COLUMN_SORT_MAP: Record<string, StaffSortField> = {
  invitedAt: 'invitedAt',
  lastSeenAt: 'lastSeenAt',
  status: 'status',
};

const outcomeToast = (results: { outcome: string; email: string }[]) => {
  if (!results?.length) return;
  const invited = results.filter(r => r.outcome !== 'error').length;
  if (invited === 0) {
    toast({ title: "Couldn't send invites", type: 'alert' });
    return;
  }
  toast({ title: `Invited ${invited} staff member${invited === 1 ? '' : 's'}` });
};

const StaffList: React.FC<Props> = ({ pageId, sellerId, canManage, title, actions }) => {
  const history = useHistory();
  const location = useLocation();

  const initialStatus = (() => {
    const urlVal = new URLSearchParams(location.search).get('status') ?? '';
    return URL_STATUS_TO_API[urlVal];
  })();
  const [statusFilter, setStatusFilter] = useState<StaffStatusFilter | undefined>(initialStatus);
  const [globalFilter, setGlobalFilter] = useState('');
  const [sorting, setSorting] = useState<SortingState>([{ id: 'invitedAt', desc: true }]);
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: PAGE_SIZE,
  });
  // Row selection keyed by userId (via DataGrid.getRowId).
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const [bulkBusy, setBulkBusy] = useState(false);

  const handleStatusChange = (next: string) => {
    const apiVal = next === 'all' ? undefined : (next as StaffStatusFilter);
    setStatusFilter(apiVal);
    setPagination(p => ({ ...p, pageIndex: 0 }));
    // Mirror the change into the URL so refresh / sharing preserves it.
    const params = new URLSearchParams(location.search);
    if (apiVal) {
      params.set('status', API_STATUS_TO_URL[apiVal]);
    } else {
      params.delete('status');
    }
    const qs = params.toString();
    history.replace(`${location.pathname}${qs ? `?${qs}` : ''}`);
  };

  const resetToFirstPage = () =>
    setPagination(p => (p.pageIndex === 0 ? p : { ...p, pageIndex: 0 }));

  const sortSpec = sorting[0];
  const apiSort: StaffSortField = sortSpec
    ? (COLUMN_SORT_MAP[sortSpec.id] ?? 'invitedAt')
    : 'invitedAt';
  const apiDir: StaffSortDir = sortSpec?.desc ? 'desc' : 'asc';

  const { page } = usePage();
  const ticketTypeOptions = useMemo(() => deriveTicketTypeOptions(page), [page]);

  const {
    invites,
    total,
    isLoading,
    isFetching,
    resend,
    revoke,
    remove,
    invite,
    updatePermissions,
    pending,
  } = useStaffInvites(pageId, sellerId, {
    page: pagination.pageIndex + 1,
    limit: pagination.pageSize,
    q: globalFilter,
    sort: apiSort,
    dir: apiDir,
    status: statusFilter,
    // The 'deleted' URL filter maps to API revoked, but the user may
    // also want to see fully-deleted rows. Let removed-mode opt in.
    includeDeleted: statusFilter === 'revoked' ? true : undefined,
  });

  const handleReinvite = async (email: string) => {
    const results = await invite([{ email }]);
    outcomeToast(results);
  };

  const handleRemove = async (userId: string, email: string) => {
    const res = (await remove(userId)) as { removedCount?: number } | undefined;
    const count = res?.removedCount ?? 0;
    toast({
      title: `Deleted ${email}`,
    });
  };

  const rowPending = (inv: StaffInvite): RowPending => ({
    resend: pending.resend === inv.userId,
    revoke: pending.revoke === inv.userId,
    remove: pending.remove === inv.userId,
    reinvite:
      pending.invite &&
      Array.isArray(pending.inviteVariables) &&
      pending.inviteVariables.length === 1 &&
      pending.inviteVariables[0].email === inv.email,
  });

  const columns = useMemo(
    () =>
      getStaffColumns({
        canManage,
        rowPending,
        ticketTypeOptions,
        onResend: id => resend(id),
        onReinvite: handleReinvite,
        onRevoke: id => revoke(id),
        onRemove: id => {
          const target = invites.find(i => i.userId === id);
          handleRemove(id, target?.email ?? '');
        },
        onUpdatePermissions: (userId, allowedTicketTypes) =>
          updatePermissions({ userId, allowedTicketTypes }),
      }),

    [canManage, pending, invites, ticketTypeOptions],
  );

  const selectedIds = Object.entries(rowSelection)
    .filter(([, v]) => v)
    .map(([id]) => id);
  const selectedInvites = invites.filter(i => selectedIds.includes(i.userId));
  const resendCandidates = selectedInvites.filter(i => i.status !== 'deleted');
  const revokeCandidates = selectedInvites.filter(
    i => i.status === 'pending' || i.status === 'active',
  );

  const handleBulkResend = async () => {
    if (resendCandidates.length === 0) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        resendCandidates.map(inv => resend(inv.userId, { silent: true })),
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      toast({
        title: `Invited ${ok} staff member${ok === 1 ? '' : 's'}`,
        description: failed > 0 ? `${failed} couldn’t be sent — try again in a minute.` : undefined,
        type: failed > 0 ? 'alert' : undefined,
      });
      setRowSelection({});
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkRevoke = async () => {
    if (revokeCandidates.length === 0) return;
    setBulkBusy(true);
    try {
      const results = await Promise.allSettled(
        revokeCandidates.map(inv => revoke(inv.userId, { silent: true })),
      );
      const ok = results.filter(r => r.status === 'fulfilled').length;
      const failed = results.length - ok;
      toast({
        title: `Removed access for ${ok} staff member${ok === 1 ? '' : 's'}`,
        description:
          failed > 0 ? `${failed} couldn’t be updated — try again in a moment.` : undefined,
        type: failed > 0 ? 'alert' : undefined,
      });
      setRowSelection({});
    } finally {
      setBulkBusy(false);
    }
  };

  const bulkActions =
    selectedIds.length > 0 ? (
      <div className="flex items-center gap-2 text-sm">
        <span className="text-muted-foreground">{selectedIds.length} selected</span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={bulkBusy || resendCandidates.length === 0}
          onClick={handleBulkResend}
        >
          {bulkBusy ? 'Working…' : `Resend invite (${resendCandidates.length})`}
        </Button>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={bulkBusy || revokeCandidates.length === 0}
          onClick={handleBulkRevoke}
        >
          {bulkBusy ? 'Working…' : `Remove access (${revokeCandidates.length})`}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={() => setRowSelection({})}>
          Clear
        </Button>
      </div>
    ) : null;

  const pageCount = Math.max(1, Math.ceil(total / pagination.pageSize));

  return (
    <DataGrid<StaffInvite>
      columns={columns}
      data={invites}
      isLoading={isLoading || isFetching}
      getRowId={row => row.userId}
      enableRowSelection={canManage}
      rowSelection={rowSelection}
      onRowSelectionChange={updater => {
        const next = typeof updater === 'function' ? updater(rowSelection) : updater;
        setRowSelection(next);
      }}
      paginationMode="page"
      pagination={pagination}
      onPaginationChange={updater => {
        const next = typeof updater === 'function' ? updater(pagination) : updater;
        setPagination(next);
      }}
      pageCount={pageCount}
      sorting={sorting}
      onSortingChange={updater => {
        const next = typeof updater === 'function' ? updater(sorting) : updater;
        setSorting(next);
        resetToFirstPage();
      }}
      globalFilter={globalFilter}
      onGlobalFilterChange={value => {
        setGlobalFilter(value);
        resetToFirstPage();
      }}
      emptyMessage="No staff invited yet."
      toolbar={{
        title,
        customActions: actions,
        showSearch: true,
        searchPlaceholder: 'Search name or email…',
        selectionBar: bulkActions,
        leftActions: (
          <Select value={statusFilter ?? 'all'} onValueChange={handleStatusChange}>
            <SelectTrigger className="h-8 w-[160px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All</SelectItem>
              <SelectItem value="pending">{STATUS_LABEL.pending}</SelectItem>
              <SelectItem value="active">{STATUS_LABEL.active}</SelectItem>
              <SelectItem value="revoked">{STATUS_LABEL.revoked}</SelectItem>
            </SelectContent>
          </Select>
        ),
      }}
    />
  );
};

export default StaffList;
